/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import { ResponsiveImage } from '@blaze-cms/image-cdn-react';
import { Link, MainContext } from '@blaze-cms/nextjs-components';

const QUERY_PROPS = `
        parent {
          id
          name
          url
          image {
            url
          }
        }
        children {
          id
          name
          url
        }`;

const getQuery = isPreview => gql`
  query getYachtForCharter($id: String!) {
    charterData: get${isPreview ? '' : 'Published'}YachtForCharter(id: $id) {
      allDestinationsHierarchy {
        ${QUERY_PROPS}
      }
      winterDestinationsHierarchy {
        ${QUERY_PROPS}
      }
      summerDestinationsHierarchy {
        ${QUERY_PROPS}
      }
    }
  }
`;

const CharterCruisingAreas = ({ parent, modifier, fallbackUrl, seasons, elementTitle }) => {
  const { isPreview } = useContext(MainContext);
  const { itemId } = parent;
  const { data = { charterData: { allDestinationsHierarchy: [] } }, loading } = useQuery(
    getQuery(isPreview),
    {
      variables: { id: itemId }
    }
  );

  const {
    allDestinationsHierarchy = [],
    winterDestinationsHierarchy = [],
    summerDestinationsHierarchy = []
  } = data.charterData;
  if (loading || !allDestinationsHierarchy.length) return null;

  const showSummer = seasons === 'summer' && summerDestinationsHierarchy.length;
  const showWinter = seasons === 'winter' && winterDestinationsHierarchy.length;
  let showAll = (!seasons || seasons === 'all') && allDestinationsHierarchy.length;
  let showSummerAndWinter =
    seasons === 'summer_and_winter' &&
    (summerDestinationsHierarchy.length || winterDestinationsHierarchy.length);

  if (!showSummer && !showWinter && !showAll && !showSummerAndWinter) return null;

  if (showSummerAndWinter) {
    if (
      // check if summer and winter are the same and if so show all year
      JSON.stringify(summerDestinationsHierarchy) === JSON.stringify(winterDestinationsHierarchy)
    ) {
      showAll = true;
      showSummerAndWinter = false;
    }
  }

  const calculateIsSingleColumn = () => {
    if (showSummerAndWinter)
      return summerDestinationsHierarchy.length < 1 || winterDestinationsHierarchy.length < 1;

    if (showAll) return allDestinationsHierarchy.length <= 1;

    if (showSummer) return summerDestinationsHierarchy.length <= 1;

    if (showWinter) return winterDestinationsHierarchy.length <= 1;

    return false;
  };
  const isSingleColumn = calculateIsSingleColumn();

  return (
    <div
      className={`charter-cruising-areas ${modifier || ''} ${
        isSingleColumn ? 'charter-cruising-areas--single' : ''
      }`}>
      {elementTitle && <h3>{elementTitle}</h3>}
      {showAll && !!allDestinationsHierarchy.length && (
        <div className="grid grid-cols-2 grid--two">
          {allDestinationsHierarchy.map(item => (
            <DestinationItem key={item.parent.id} fallbackUrl={fallbackUrl} {...item} />
          ))}
        </div>
      )}

      {showSummer && (
        <>
          <h4>Summer destinations</h4>
          <div className="grid grid-cols-2 grid--two">
            {summerDestinationsHierarchy.map(item => (
              <DestinationItem key={item.parent.id} fallbackUrl={fallbackUrl} {...item} />
            ))}
          </div>
        </>
      )}

      {showWinter && (
        <>
          <h4>Winter destinations</h4>
          <div className="grid grid-cols-2 grid--two">
            {winterDestinationsHierarchy.map(item => (
              <DestinationItem key={item.parent.id} fallbackUrl={fallbackUrl} {...item} />
            ))}
          </div>
        </>
      )}

      {showSummerAndWinter && (
        <div className="grid grid-cols-2 grid--two">
          {!!summerDestinationsHierarchy.length && (
            <div>
              <h4>Summer destinations</h4>
              <div className="grid">
                {summerDestinationsHierarchy.map(item => (
                  <DestinationItem key={item.parent.id} fallbackUrl={fallbackUrl} {...item} />
                ))}
              </div>
            </div>
          )}

          {!!winterDestinationsHierarchy.length && (
            <div>
              <h4>Winter destinations</h4>
              <div className="grid">
                {winterDestinationsHierarchy.map(item => (
                  <DestinationItem key={item.parent.id} fallbackUrl={fallbackUrl} {...item} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CharterCruisingAreas.propTypes = {
  modifier: PropTypes.string,
  fallbackUrl: PropTypes.string,
  parent: PropTypes.object.isRequired,
  seasons: PropTypes.string,
  elementTitle: PropTypes.string
};

CharterCruisingAreas.defaultProps = {
  modifier: '',
  fallbackUrl: '',
  seasons: 'all',
  elementTitle: ''
};

const getDesintaitonUrl = (fallbackUrl, url, name) => {
  if (url) return url;

  return fallbackUrl ? `${fallbackUrl}${encodeURIComponent(name)}` : null;
};

// eslint-disable-next-line react/prop-types
const DestinationItem = ({ fallbackUrl, parent: { id, name, url, image } = {}, children = [] }) => {
  const href = getDesintaitonUrl(fallbackUrl, url, name);
  const LinkTag = href ? Link : 'div';
  return (
    <div className="card card--landscape" key={id}>
      {image && (
        <LinkTag href={href} className="card__image card__image--landscape">
          <ResponsiveImage
            src={image.url}
            alt={image.data?.alt || name}
            sizeKey="card:landscape:cruising-areas"
          />
        </LinkTag>
      )}
      <div className="card__content-wrapper card__content-wrapper--landscape">
        <div className="card__content card__content--landscape">
          <LinkTag
            href={getDesintaitonUrl(fallbackUrl, url, name)}
            className="card__title card__title--landscape">
            {name}
          </LinkTag>
        </div>
        <div className="card__child-content-cursing-area">
          {children.map(({ id: childId, name: childName, url: childUrl }) => (
            <ChildDestinationItem
              childId={childId}
              childName={childName}
              childUrl={childUrl}
              fallbackUrl={fallbackUrl}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line react/prop-types
const ChildDestinationItem = ({ childId, childName, childUrl, fallbackUrl }) => {
  const urlToUse = getDesintaitonUrl(fallbackUrl, childUrl, childName);
  return urlToUse ? (
    <Link href={getDesintaitonUrl(fallbackUrl, childUrl, childName)} key={childId}>
      {childName}
    </Link>
  ) : (
    <span key={childId}>{childName}</span>
  );
};

export default CharterCruisingAreas;
